// ── 요약 화면 데이터 조회 훅 ──────────────────────────────────────────────────
//   TmSummary.jsx 에서 분리(300줄 규칙). 조회 동작·에러 처리는 분리 전과 동일합니다.
//   경로 빌더(buildXxxPath)를 props 로 주입받아 직원 본인 / 관리자 모달 / 정산서가 같은 훅을 쓴다.
import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../api/apiClient';

export function useSummaryData({
  year, month, dateRange, toast,
  buildSummaryPath, buildCallsPath, buildKpiPath, buildScorePath,
  onBeforeSummary,
}) {
  // 결과유형별 통계 (차트·테이블)
  const [stats,   setStats]   = useState([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(true);
  // 달력용 월별 콜
  const [calCalls,   setCalCalls]   = useState([]);
  const [calLoading, setCalLoading] = useState(true);
  // KPI (contracts 테이블 기준: 신규/재주문 분리 집계)
  const [kpiData,    setKpiData]    = useState(null);
  const [kpiLoading, setKpiLoading] = useState(true);
  // 월간 점수
  const [scoreData, setScoreData] = useState(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    onBeforeSummary?.();
    try {
      const res = await apiClient.get(buildSummaryPath({ year, month, dateRange }));
      if (res.data?.success) {
        setStats(res.data.stats || []);
        setTotal(res.data.total ?? 0);
      } else {
        toast.error(res.data?.message || '통계를 불러오지 못했습니다.');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || '통계 조회 실패');
    } finally {
      setLoading(false);
    }
  }, [buildSummaryPath, dateRange, month, toast, year, onBeforeSummary]);

  const fetchCalendarCalls = useCallback(async () => {
    setCalLoading(true);
    try {
      const res = await apiClient.get(buildCallsPath({ year, month }));
      if (res.data?.success) {
        setCalCalls(res.data.data || []);
      } else {
        toast.error(res.data?.message || '달력 데이터를 불러오지 못했습니다.');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || '달력 데이터 조회 실패');
    } finally {
      setCalLoading(false);
    }
  }, [buildCallsPath, year, month, toast]);

  // contracts 테이블 직접 집계 — 콜 중심 API로는 재주문 누락됨
  const fetchKpi = useCallback(async () => {
    setKpiLoading(true);
    try {
      const res = await apiClient.get(buildKpiPath({ year, month }));
      if (res.data?.success) {
        setKpiData(res.data);
      } else {
        toast.error(res.data?.message || 'KPI 데이터를 불러오지 못했습니다.');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'KPI 조회 실패');
    } finally {
      setKpiLoading(false);
    }
  }, [buildKpiPath, year, month, toast]);

  // 월간 점수 — 회사·월에 따라 없는 것이 정상이므로 실패해도 토스트를 띄우지 않는다.
  //   buildScorePath 가 null 이면 조회 자체를 하지 않는다(점수 비노출 화면).
  const fetchScore = useCallback(async () => {
    if (!buildScorePath) { setScoreData(null); return; }
    try {
      const res = await apiClient.get(buildScorePath({ year, month }));
      setScoreData(res.data?.success ? res.data : null);
    } catch {
      setScoreData(null);
    }
  }, [buildScorePath, year, month]);

  useEffect(() => { fetchSummary();       }, [fetchSummary]);
  useEffect(() => { fetchCalendarCalls(); }, [fetchCalendarCalls]);
  useEffect(() => { fetchKpi();           }, [fetchKpi]);
  useEffect(() => { fetchScore();         }, [fetchScore]);

  return { stats, total, loading, calCalls, calLoading, kpiData, kpiLoading, scoreData };
}
